import { cloneDeep, isNumber, isUndefined, toInteger } from 'lodash';
import { trackConfig } from '@/framework';
import {
    BrushDasharrayType,
    BrushLinecapType,
    BrushType,
    IBrushDrawConfig,
    IBrushLayerProps,
    IBrushPoint,
    IExportPlugin,
    ILayerProps,
    IPoint,
    LayerType,
    PluginType,
} from '@/version-types';
import { Layer, layerPlugin } from '../layer';
import brushLayerBehavior from './behavior';
import {
    addVipMaterialIds,
    getBrushBaseInfo,
    getBrushDefaultLayer,
    getPathOffset,
    isArrowSvg,
    isCurveSvg,
    isWavySvg,
    scaleConfig,
    setColor,
    setPoint,
    setRadius,
    setStyle,
    setSwap,
    setWidth,
    updateScreenshotUrl,
} from './utils';

export * from './utils';
export { default as BrushLayerView } from './views/brush-layer';

export class BrushLayer extends Layer {
    isCurvePen(layer: ILayerProps) {
        const { brushType } = layer.data as IBrushLayerProps;
        return isCurveSvg(brushType);
    }

    isArrowPen(layer: ILayerProps) {
        const { brushType } = layer.data as IBrushLayerProps;
        return isArrowSvg(brushType);
    }

    isWavyPen(layer: ILayerProps) {
        const { brushType } = layer.data as IBrushLayerProps;
        return isWavySvg(brushType);
    }

    isRect(layer: ILayerProps) {
        const { brushType } = layer.data as IBrushLayerProps;
        return brushType === BrushType.Rect;
    }

    /**
     * 添加画笔直线图层
     * @param info - 画笔配置
     * @param rotate - 旋转角度
     * @param byMousePosition - 是否通过鼠标位置添加
     */
    async addPresetLine(info: IBrushDrawConfig, rotate = -45, byMousePosition = false) {
        const { width: stageWidth, height: stageHeight } = this.framework.stage.getActiveStageSize();
        info = scaleConfig(info, stageWidth);
        const width = toInteger(Math.min(stageWidth, stageHeight) / 2);
        const layer = getBrushDefaultLayer(info);
        const data = layer.data as IBrushLayerProps;
        if (info.brushType === BrushType.Line) {
            data.naturalWidth = width;
            data.naturalHeight = 1;
            const res = getPathOffset(data.fills, data.stroke);
            layer.width = width;
            layer.height = data.naturalHeight + res.offsetHeight;
        } else if (data.brushType === BrushType.Arrow) {
            const brushInfoData = getBrushBaseInfo(
                this.identity,
                info,
                1,
                { x: 0, y: 0 },
                { x: width, y: 0 },
                0,
                false,
            );
            data.naturalWidth = brushInfoData.naturalWidth;
            data.naturalHeight = brushInfoData.naturalHeight;
            layer.width = ~~brushInfoData.width;
            layer.height = ~~brushInfoData.height;
        }
        if (isNumber(rotate)) {
            layer.rotate = rotate;
        }
        addVipMaterialIds(layer, info);
        const finalLayer = byMousePosition ? this.addByMousePosition(layer) : this.addByDiagonal(layer);
        updateScreenshotUrl(this.identity, finalLayer.id);
    }

    async setColor(id: string, color: string, isStroke = false, index = 0, originColor?: string) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        let oldLayer = layer;
        if (!isUndefined(originColor)) {
            oldLayer = cloneDeep(layer);
        }
        setColor(layer, color, isStroke, index);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        if (!isUndefined(originColor)) {
            setColor(oldLayer, originColor, isStroke, index);
            this.framework.history.write(
                oldLayer,
                trackConfig[LayerType.BRUSH_LAYER][`${isStroke ? 'stroke_' : ''}color`],
            );
            updateScreenshotUrl(this.identity, id);
        }
    }

    async setRadius(id: string, radius: number, originRadius?: number) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        let oldLayer = layer;
        if (isNumber(originRadius)) {
            oldLayer = cloneDeep(layer);
        }
        setRadius(layer, radius);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        if (isNumber(originRadius)) {
            setRadius(oldLayer, originRadius);
            this.framework.history.write(oldLayer, trackConfig[LayerType.BRUSH_LAYER].radius);
            updateScreenshotUrl(this.identity, id);
        }
    }

    setWidth(id: string, width: number, centerPoint: IPoint, isStroke = false, oldLayer?: ILayerProps) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        setWidth(layer, width, centerPoint, isStroke);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        if (!isUndefined(oldLayer)) {
            this.framework.history.write(
                oldLayer,
                trackConfig[LayerType.BRUSH_LAYER][`${isStroke ? 'stroke_' : ''}width`],
            );
            updateScreenshotUrl(this.identity, id);
        }
    }

    setStyle(id: string, dasharray: BrushDasharrayType, linecap: BrushLinecapType, isStroke = false) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setStyle(layer, dasharray, linecap, isStroke);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.BRUSH_LAYER][`${isStroke ? 'stroke_' : ''}style`]);
        updateScreenshotUrl(this.identity, id);
    }

    setPoint(id: string, point: IBrushPoint, isStartPoint: boolean) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setPoint(layer, point, isStartPoint);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.BRUSH_LAYER].point);
        updateScreenshotUrl(this.identity, id);
    }

    swapPoint(id: string) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        const data = layer.data as IBrushLayerProps;
        const endPoint = cloneDeep(data.startPoint);
        const startPoint = cloneDeep(data.endPoint);
        setSwap(layer, startPoint, endPoint);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.BRUSH_LAYER].point);
        updateScreenshotUrl(this.identity, id);
    }
}

/**
 * 画笔图层插件
 */
export const brushLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.BRUSH_LAYER,
    createInstance({ identity }) {
        return [{ key: 'BrushLayer', value: new BrushLayer(identity) }];
    },
    createBehavior() {
        return brushLayerBehavior;
    },
};
